// src/components/SmartAlertSettings.jsx
import React, { useState, useEffect, useCallback } from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import E164PhoneInput from "./E164PhoneInput";

// Single independent Smart Alert Settings API boundary: load/save/review
import {
  loadSmartAlertSettings,
  saveSmartAlertSettings,
  requestSmartAlertReview,
  loadSmartAlertReviewStatus,
} from "../utils/saveSmartAlertSettings";


/* ------------------------------ helpers ------------------------------ */
const safeStr = (v) => (v ?? "").toString();

const normalizeContact = (c = {}) => ({
  name: safeStr(c.name),
  email: safeStr(c.email),
  phone: safeStr(c.phone),
  message: safeStr(c.message),
});

const normalizeContacts = (arr) =>
  Array.isArray(arr) && arr.length > 0
    ? arr.map(normalizeContact)
    : [normalizeContact()];

const isValidEmail = (email) =>
  /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(safeStr(email).trim());

const normalizePhone = (raw) => {
  if (!raw) return null;
  const s = safeStr(raw);
  const digits = s.replace(/[^\d]/g, "");
  if (!digits) return null;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  if (s.trim().startsWith("+")) return s.trim();
  return `+${digits}`;
};

const upsertExpanded = (expanded, targetLen) => {
  const arr = Array.isArray(expanded) ? [...expanded] : [];
  while (arr.length < targetLen) arr.push(true);
  return arr.slice(0, targetLen);
};


const emptyReviewState = () => ({
  email: null,
  phone: null,
});

const upsertReviewState = (state, targetLen) => {
  const arr = Array.isArray(state)
    ? state.map((item) => ({
        email: item?.email || null,
        phone: item?.phone || null,
      }))
    : [];

  while (arr.length < targetLen) arr.push(emptyReviewState());
  return arr.slice(0, targetLen);
};

const loadReviewStatesForContacts = async (contacts, denyInfo) => {
  const next = contacts.map(() => emptyReviewState());
  const requests = [];

  contacts.forEach((contact, index) => {
    const info = denyInfo?.[index] || {};

    if (info?.email_denied && safeStr(contact?.email).trim()) {
      requests.push(
        loadSmartAlertReviewStatus({
          kind: "email",
          value: safeStr(contact.email).trim(),
        })
          .then((result) => {
            next[index].email = result?.review || null;
          })
          .catch((error) => {
            console.warn("⚠️ Smart Alert email review-status lookup failed:", error);
          })
      );
    }

    if (info?.phone_denied && safeStr(contact?.phone).trim()) {
      requests.push(
        loadSmartAlertReviewStatus({
          kind: "phone",
          value: normalizePhone(contact.phone) || safeStr(contact.phone).trim(),
        })
          .then((result) => {
            next[index].phone = result?.review || null;
          })
          .catch((error) => {
            console.warn("⚠️ Smart Alert phone review-status lookup failed:", error);
          })
      );
    }
  });

  if (requests.length > 0) {
    await Promise.all(requests);
  }

  return next;
};

/* ------------------------------ Inline Banner ------------------------------ */
const Banner = ({ type = "info", children, onClose }) => {
  const palette =
    {
      success: { bg: "#ecfdf5", border: "#10b981", text: "#065f46" },
      error: { bg: "#fef2f2", border: "#ef4444", text: "#991b1b" },
      info: { bg: "#eef2ff", border: "#6366f1", text: "#3730a3" },
      warn: { bg: "#fffbeb", border: "#f59e0b", text: "#92400e" },
    }[type] || { bg: "#eef2ff", border: "#6366f1", text: "#3730a3" };

  return (
    <div
      role="status"
      aria-live={type === "error" ? "assertive" : "polite"}
      style={{
        background: palette.bg,
        borderLeft: `4px solid ${palette.border}`,
        color: palette.text,
        padding: "11px 13px",
        borderRadius: 14,
        width: "100%",
        maxWidth: 560,
        margin: "0 auto 12px",
        boxSizing: "border-box",
        boxShadow: "0 8px 20px rgba(2,6,23,0.045)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <div style={{ fontSize: 13.5, lineHeight: "19px", fontWeight: 750 }}>{children}</div>
        {onClose && (
          <button
            onClick={onClose}
            aria-label="Dismiss"
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontSize: 16,
              lineHeight: "16px",
              color: palette.text,
            }}
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};

/* --------------------------- contact subform -------------------------- */
const ContactInput = ({
  index,
  contact,
  onChange,
  isExpanded,
  toggleExpand,
  onRequestRemove,
  denyInfo,
  reviewState,
  onRequestReview,
  reviewBusy,
}) => {
  const nameStr = safeStr(contact.name);
  const emailStr = safeStr(contact.email);
  const phoneStr = safeStr(contact.phone);
  const messageStr = safeStr(contact.message);

  const emailDenied = !!denyInfo?.email_denied;
  const phoneDenied = !!denyInfo?.phone_denied;

  const emailUiState = denyInfo?.email_ui_state || null;
  const phoneUiState = denyInfo?.phone_ui_state || null;

  const emailReview = reviewState?.email || null;
  const phoneReview = reviewState?.phone || null;

  const emailReviewStatus = safeStr(emailReview?.status).trim().toLowerCase();
  const phoneReviewStatus = safeStr(phoneReview?.status).trim().toLowerCase();

  const emailRecipientActionRequired =
    emailUiState?.status === "recipient_action_required" ||
    emailUiState?.can_request_review === false;

  const phoneRecipientActionRequired =
    phoneUiState?.status === "recipient_action_required" ||
    phoneUiState?.can_request_review === false;

  const emailReviewPending = emailReviewStatus === "pending";
  const phoneReviewPending = phoneReviewStatus === "pending";
  const emailReviewDenied = emailReviewStatus === "denied";
  const phoneReviewDenied = phoneReviewStatus === "denied";

  const handleCopy = () => {
    navigator.clipboard.writeText(messageStr);
  };

  const missingMethod =
    nameStr.trim() !== "" && emailStr.trim() === "" && phoneStr.trim() === "";

  return (
    <div style={styles.contactCard}>
      <button type="button" onClick={toggleExpand} style={styles.contactHeader}>
        <div style={styles.contactHeaderLeft}>
          <span style={styles.contactNumber}>{index + 1}</span>
          <div>
            <div style={styles.contactTitle}>
              {nameStr.trim() ? nameStr.trim() : `Contact ${index + 1}`}
            </div>
            <div style={styles.contactSubtitle}>
              {emailStr || phoneStr
                ? [emailStr, phoneStr].filter(Boolean).join(" • ")
                : "Add email or phone number"}
            </div>
          </div>
        </div>
        <span style={styles.expandIcon}>
          {isExpanded ? "Hide" : "Show"}
        </span>
      </button>

      {isExpanded && (
        <div style={styles.contactForm}>
          <label style={styles.fieldGroup}>
            <span style={styles.label}>Full Name</span>
            <input
              type="text"
              placeholder="Full Name"
              value={nameStr}
              onChange={(e) => onChange(index, "name", e.target.value)}
              style={styles.input}
            />
          </label>

          <label style={{ ...styles.fieldGroup, position: "relative" }}>
            <span style={styles.label}>Email</span>
            <input
              type="email"
              placeholder="Email"
              value={emailStr}
              onChange={(e) => onChange(index, "email", e.target.value)}
              style={{
                ...styles.input,
                borderColor:
                  (emailStr && !isValidEmail(emailStr)) || emailDenied
                    ? "#f87171"
                    : "rgba(2,6,23,0.16)",
              }}
              aria-invalid={
                (emailStr && !isValidEmail(emailStr)) || emailDenied
                  ? "true"
                  : "false"
              }
            />
            {emailStr && !isValidEmail(emailStr) && (
              <div style={styles.emailError}>Invalid email format</div>
            )}
            {emailDenied && (
              <div style={styles.blockedPanel}>
                <div style={styles.blockedTitle}>
                  {emailRecipientActionRequired
                    ? "Recipient Opt-Out — Recipient Action Required"
                    : emailReviewPending
                    ? "Review Requested"
                    : emailReviewDenied
                    ? "Review Denied — Update Contact or Request Again"
                    : emailUiState?.label || "Blocked — Update Contact or Request Review"}
                </div>

                <div style={styles.blockedText}>
                  {emailRecipientActionRequired
                    ? "This email cannot be re-enabled by the sender. The recipient must use the provider-supported re-subscribe process."
                    : emailReviewPending
                    ? "This email remains blocked while the review is pending."
                    : emailReviewDenied
                    ? "The email remains blocked. You can update the address or submit another review request."
                    : "This email is blocked from Smart Alert delivery. Update the address or request a review if you believe the block is incorrect."}
                </div>

                {!emailRecipientActionRequired && !emailReviewPending && (
                  <button
                    type="button"
                    onClick={() => onRequestReview(index, "email")}
                    disabled={!!reviewBusy?.email}
                    style={{
                      ...styles.reviewBtn,
                      ...(reviewBusy?.email ? styles.reviewBtnDisabled : {}),
                    }}
                  >
                    {reviewBusy?.email
                      ? "Requesting…"
                      : emailReviewDenied
                      ? "Request Review Again"
                      : "Request Review"}
                  </button>
                )}
              </div>
            )}
          </label>

          {/* ✅ Phone input: E.164, US-only hint */}
          <div style={styles.fieldGroup}>
            <span style={styles.label}>Phone</span>
            <E164PhoneInput
              value={phoneStr || ""}
              onChange={(value) => onChange(index, "phone", safeStr(value))}
            />
            <div style={styles.hint}>
              US only for now. Numbers are saved in +1XXXXXXXXXX format.
            </div>
            {phoneDenied && (
              <div style={styles.blockedPanel}>
                <div style={styles.blockedTitle}>
                  {phoneRecipientActionRequired
                    ? "Recipient Opt-Out — Recipient Action Required"
                    : phoneReviewPending
                    ? "Review Requested"
                    : phoneReviewDenied
                    ? "Review Denied — Update Contact or Request Again"
                    : phoneUiState?.label || "Blocked — Update Contact or Request Review"}
                </div>

                <div style={styles.blockedText}>
                  {phoneRecipientActionRequired
                    ? "This number cannot be re-enabled by the sender. The recipient must re-enable SMS through the provider-supported process."
                    : phoneReviewPending
                    ? "This phone number remains blocked while the review is pending."
                    : phoneReviewDenied
                    ? "The phone number remains blocked. You can update it or submit another review request."
                    : "This phone number is blocked from Smart Alert delivery. Update it or request a review if you believe the block is incorrect."}
                </div>

                {!phoneRecipientActionRequired && !phoneReviewPending && (
                  <button
                    type="button"
                    onClick={() => onRequestReview(index, "phone")}
                    disabled={!!reviewBusy?.phone}
                    style={{
                      ...styles.reviewBtn,
                      ...(reviewBusy?.phone ? styles.reviewBtnDisabled : {}),
                    }}
                  >
                    {reviewBusy?.phone
                      ? "Requesting…"
                      : phoneReviewDenied
                      ? "Request Review Again"
                      : "Request Review"}
                  </button>
                )}
              </div>
            )}
          </div>

          {missingMethod && (
            <div style={styles.validationWarning}>
              Please add at least one email address or phone number.
            </div>
          )}

          <label style={styles.textareaWrapper}>
            <span style={styles.label}>Custom Message</span>
            <textarea
              placeholder="Custom message (max 300 chars)"
              value={messageStr}
              onChange={(e) =>
                e.target.value.length <= 300 &&
                onChange(index, "message", e.target.value)
              }
              style={styles.textarea}
              maxLength={300}
            />
          </label>

          <div style={styles.copyMetaRow}>
            <div style={styles.charCount}>{messageStr.length} / 300</div>
            <div style={styles.copyBlock}>
            <span
              data-tip="Copy to clipboard"
              style={styles.copyIcon}
              onClick={handleCopy}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="#555"
                viewBox="0 0 24 24"
              >
                <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1z"></path>
                <path d="M20 5H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h12v14z"></path>
              </svg>
            </span>
            <ReactTooltip place="top" type="dark" effect="solid" />
            </div>
          </div>

          {index > 0 && (
            <button
              onClick={() => onRequestRemove(index)}
              style={styles.removeBtn}
            >
              ❌ Remove Contact
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/* --------------------------- main settings UI -------------------------- */
const SmartAlertSettings = () => {
  const [contacts, setContacts] = useState(() => {
    const saved = localStorage.getItem("smart_alert_contacts");
    return normalizeContacts(
      saved ? JSON.parse(saved) : [{ name: "", email: "", phone: "", message: "" }]
    );
  });
  const [expanded, setExpanded] = useState(() => {
    const saved = localStorage.getItem("smart_alert_expanded");
    return saved ? JSON.parse(saved) : [true];
  });

  // Backend-derived deny information. Never persisted to localStorage.
  const [denyInfo, setDenyInfo] = useState([]);

  // Review state is also backend-authoritative and is not persisted locally.
  const [reviewState, setReviewState] = useState([]);
  const [reviewBusy, setReviewBusy] = useState({});

  const [isSaving, setIsSaving] = useState(false);
  const [banner, setBanner] = useState(null); // {type,msg}
  const [confirmRemoveIndex, setConfirmRemoveIndex] = useState(null);

  // Persist an editable local draft; backend remains the source of truth
  useEffect(() => {
    const norm = normalizeContacts(contacts);
    localStorage.setItem("smart_alert_contacts", JSON.stringify(norm));
    localStorage.setItem("smart_alert_expanded", JSON.stringify(expanded));
  }, [contacts, expanded]);

  // 🔄 Load from backend on mount (and fix expanded length to match)
  const hydrateFromServer = useCallback(async () => {
    try {
      setBanner({ type: "info", msg: "Loading contacts…" });
      const result = await loadSmartAlertSettings(); // { success, contacts, deny_info }

      if (result?.success) {
        const serverContacts = normalizeContacts(result.contacts || []);
        const serverDeny = Array.isArray(result.deny_info) ? result.deny_info : [];

        setContacts(serverContacts);
        setDenyInfo(serverDeny);
        setExpanded((prev) => upsertExpanded(prev, serverContacts.length));

        const serverReviewState = await loadReviewStatesForContacts(
          serverContacts,
          serverDeny
        );
        setReviewState(serverReviewState);

        // Persist synced contact draft only; deny/review state remains backend-owned.
        localStorage.setItem("smart_alert_contacts", JSON.stringify(serverContacts));
        localStorage.setItem(
          "smart_alert_expanded",
          JSON.stringify(upsertExpanded(expanded, serverContacts.length))
        );

        setBanner(null);
      } else {
        throw new Error(result?.error || "Failed to load contacts.");
      }
    } catch (err) {
      console.error("❌ Load failed:", err);
      setBanner({
        type: "error",
        msg: "Couldn’t load saved contacts. Please refresh or try again.",
      });
    }
  }, []); // run once

  useEffect(() => {
  hydrateFromServer();
  // Also reload when tab becomes visible again (helps stale UIs)
  const onVis = () => {
    if (document.visibilityState === "visible") {
      hydrateFromServer();
    }
  };
  document.addEventListener("visibilitychange", onVis);
  return () => document.removeEventListener("visibilitychange", onVis);
}, [hydrateFromServer]);


  const updateContact = (index, field, value) => {
    const updated = normalizeContacts(contacts);
    updated[index][field] = safeStr(value);
    setContacts(updated);

    if (field === "email" || field === "phone") {
      setDenyInfo((prev) => {
        const next = Array.isArray(prev) ? [...prev] : [];
        const current = { ...(next[index] || {}) };

        if (field === "email") {
          current.email_denied = false;
          current.email_doc = null;
          current.email_ui_state = null;
        } else {
          current.phone_denied = false;
          current.phone_doc = null;
          current.phone_ui_state = null;
        }

        current.has_blocked_channel =
          !!current.email_denied || !!current.phone_denied;

        next[index] = current;
        return next;
      });

      setReviewState((prev) => {
        const next = upsertReviewState(prev, contacts.length);
        next[index] = {
          ...next[index],
          [field === "email" ? "email" : "phone"]: null,
        };
        return next;
      });
    }
  };

  const handleRequestReview = async (index, kind) => {
    const contact = normalizeContact(contacts[index] || {});
    const value =
      kind === "email"
        ? safeStr(contact.email).trim()
        : normalizePhone(contact.phone) || safeStr(contact.phone).trim();

    if (!value) {
      setBanner({
        type: "warn",
        msg:
          kind === "email"
            ? "Save a valid email address before requesting review."
            : "Save a valid phone number before requesting review.",
      });
      return;
    }

    const busyKey = `${index}:${kind}`;

    setReviewBusy((prev) => ({
      ...prev,
      [busyKey]: true,
    }));

    try {
      const result = await requestSmartAlertReview({ kind, value });
      const review = result?.review || { status: "pending" };

      setReviewState((prev) => {
        const next = upsertReviewState(prev, contacts.length);
        next[index] = {
          ...next[index],
          [kind]: review,
        };
        return next;
      });

      setBanner({
        type: "success",
        msg: result?.already_pending
          ? "A review request is already pending for this destination."
          : "Review request submitted. The destination will remain blocked until the review is completed.",
      });
    } catch (err) {
      console.error("❌ Smart Alert review request failed:", err);

      const errorCode =
        err?.payload?.error_code ||
        err?.code ||
        "";

      if (errorCode === "smart_alert_recipient_action_required") {
        setBanner({
          type: "warn",
          msg:
            err?.message ||
            "This destination was blocked by recipient opt-out or complaint action. The recipient must re-enable delivery.",
        });
        return;
      }

      if (errorCode === "smart_alert_destination_not_blocked") {
        setBanner({
          type: "info",
          msg:
            "This destination is no longer blocked. Refreshing the saved contact status.",
        });

        await hydrateFromServer();
        return;
      }

      if (errorCode === "smart_alert_review_contact_mismatch") {
        setBanner({
          type: "warn",
          msg:
            "Save this contact first, then request review of the blocked destination.",
        });
        return;
      }

      setBanner({
        type: "error",
        msg:
          err?.message ||
          "Couldn’t submit the Smart Alert review request. Please try again.",
      });
    } finally {
      setReviewBusy((prev) => {
        const next = { ...prev };
        delete next[busyKey];
        return next;
      });
    }
  };

  const toggleExpand = (index) => {
    setExpanded((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  const handleAddContact = () => {
    if (contacts.length >= 5) return;
    const nextContacts = [...normalizeContacts(contacts), normalizeContact()];
    setContacts(nextContacts);
    setExpanded((prev) => upsertExpanded(prev, nextContacts.length));
    setDenyInfo((prev) => [...prev, null]);
    setReviewState((prev) => [
      ...upsertReviewState(prev, contacts.length),
      emptyReviewState(),
    ]);
  };

  // Inline confirm flow
  const requestRemoveContact = (index) => {
    setConfirmRemoveIndex(index);
    setBanner(null);
  };

  const cancelRemove = () => setConfirmRemoveIndex(null);

  const confirmRemove = () => {
    const index = confirmRemoveIndex;
    if (index === null) return;
    const newContacts = contacts.filter((_, i) => i !== index);
    const newExpanded = expanded.filter((_, i) => i !== index);
    const newDeny = denyInfo.filter((_, i) => i !== index);
    const newReviewState = reviewState.filter((_, i) => i !== index);

    setContacts(normalizeContacts(newContacts));
    setExpanded(newExpanded);
    setDenyInfo(newDeny);
    setReviewState(newReviewState);
    setConfirmRemoveIndex(null);
    setBanner({ type: "info", msg: "Contact removed." });
  };

  const validateContacts = () => {
    const list = normalizeContacts(contacts);
    if (list.length === 0) {
      setBanner({ type: "warn", msg: "At least one contact is required." });
      return false;
    }
    for (const [i, c] of list.entries()) {
      const hasPhone = safeStr(c.phone).trim() !== "";
      const hasEmail = safeStr(c.email).trim() !== "";
      if (!hasPhone && !hasEmail) {
        setBanner({
          type: "warn",
          msg: `Contact ${i + 1} must have either a phone or email.`,
        });
        return false;
      }
      if (c.email && !isValidEmail(c.email)) {
        setBanner({
          type: "warn",
          msg: `Contact ${i + 1} has an invalid email address.`,
        });
        return false;
      }
    }
    setBanner(null);
    return true;
  };

  const handleSave = async () => {
    if (!validateContacts()) return;

    setIsSaving(true);
    setBanner({ type: "info", msg: "Saving contacts…" });

    const finalData = normalizeContacts(contacts).map((c) => ({
      ...c,
      phone: normalizePhone(c.phone),
      email: safeStr(c.email).trim() || null,
      message: safeStr(c.message).trim(),
    }));

    try {
      // Backend is authoritative:
      // - validates/normalizes contacts
      // - checks active monthly Smart Alert quota
      // - falls back to non-expiring Smart Alert add-on quota
      // - refuses to save when neither source has usable credits
      const result = await saveSmartAlertSettings(finalData);

      if (!result?.success) {
        const error = new Error(result?.error || "Save failed");
        error.code = result?.error_code || "";
        error.payload = result;
        throw error;
      }

      const serverContacts = normalizeContacts(result.contacts || []);
      const serverDeny = Array.isArray(result.deny_info) ? result.deny_info : [];

      setContacts(serverContacts);
      setDenyInfo(serverDeny);
      setExpanded((prev) => upsertExpanded(prev, serverContacts.length));

      const serverReviewState = await loadReviewStatesForContacts(
        serverContacts,
        serverDeny
      );
      setReviewState(serverReviewState);

      // Persist only the server-confirmed saved configuration.
      localStorage.setItem(
        "smart_alert_contacts",
        JSON.stringify(serverContacts)
      );
      localStorage.setItem(
        "smart_alert_expanded",
        JSON.stringify(upsertExpanded(expanded, serverContacts.length))
      );

      setBanner({
        type: "success",
        msg: "✅ Contacts saved successfully.",
      });
    } catch (err) {
      console.error("❌ Save failed:", err);

      const backendData =
        err?.response?.data ||
        err?.payload ||
        {};

      const errorCode =
        backendData?.error_code ||
        err?.code ||
        "";

      const backendMessage =
        backendData?.error ||
        err?.message ||
        "";

      if (errorCode === "smart_alert_access_required") {
        setBanner({
          type: "warn",
          msg:
            backendMessage ||
            "Smart Alert isn’t currently available on your account. " +
              "An active Smart Alert allowance or purchased add-on credits " +
              "are required to save these settings.",
        });
        return;
      }

      if (errorCode === "invalid_smart_alert_configuration") {
        setBanner({
          type: "warn",
          msg:
            backendMessage ||
            "Please review your Smart Alert contacts and try again.",
        });
        return;
      }

      setBanner({
        type: "error",
        msg:
          backendMessage && backendMessage !== "Request failed with status code 403"
            ? backendMessage
            : "Couldn’t save contacts. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const anyDenied = denyInfo?.some?.(
    (info) => info?.email_denied || info?.phone_denied
  );

  const anyDenyLookupUnavailable = denyInfo?.some?.(
    (info) => info?.lookup_unavailable
  );

  return (
    <div style={styles.wrapper}>
      {/* Top status area */}
      {banner && (
        <Banner type={banner.type} onClose={() => setBanner(null)}>
          {banner.msg}
        </Banner>
      )}

      {anyDenyLookupUnavailable && (
        <Banner type="warn">
          Smart Alert could not verify the latest blocked-contact status. Refresh
          before relying on these settings.
        </Banner>
      )}

      {anyDenied && (
        <Banner type="warn">
          One or more delivery channels are blocked. Update the blocked
          destination or request review where available. Recipient opt-outs
          require action by the recipient.
        </Banner>
      )}

      {contacts.map((contact, i) => (
        <ContactInput
          key={i}
          index={i}
          contact={normalizeContact(contact)}
          onChange={updateContact}
          onRequestRemove={requestRemoveContact}
          isExpanded={!!expanded[i]}
          toggleExpand={() => toggleExpand(i)}
          denyInfo={denyInfo?.[i] || null}
          reviewState={reviewState?.[i] || emptyReviewState()}
          onRequestReview={handleRequestReview}
          reviewBusy={{
            email: !!reviewBusy[`${i}:email`],
            phone: !!reviewBusy[`${i}:phone`],
          }}
        />
      ))}

      {/* Inline remove confirmation */}
      {confirmRemoveIndex !== null && (
        <div
          style={{
            width: "100%",
            maxWidth: 560,
            boxSizing: "border-box",
            margin: "6px 0 10px",
            padding: "10px 12px",
            borderRadius: 8,
            background: "#fffbeb",
            border: "1px solid #f59e0b",
            color: "#92400e",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ fontSize: 14 }}>
            Remove contact #{confirmRemoveIndex + 1}? This can’t be undone.
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={confirmRemove}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: "none",
                background: "#ef4444",
                color: "#fff",
                cursor: "pointer",
                fontSize: 14,
              }}
            >
              Remove
            </button>
            <button
              onClick={cancelRemove}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid #ccc",
                background: "#fff",
                color: "#111827",
                cursor: "pointer",
                fontSize: 14,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div style={styles.actions}>
        <button
          onClick={handleAddContact}
          style={{
            ...styles.button,
            backgroundColor: contacts.length >= 5 ? "#ccc" : "#1E2A78",
            color: "#fff",
            cursor: contacts.length >= 5 || isSaving ? "not-allowed" : "pointer",
          }}
          disabled={contacts.length >= 5 || isSaving}
        >
          + Add Another Contact
        </button>
        <button onClick={handleSave} style={styles.saveBtn} disabled={isSaving}>
          {isSaving ? "Saving…" : "Save All"}
        </button>
      </div>
    </div>
  );
};

/* ------------------------------ styles ------------------------------ */
const styles = {
  wrapper: {
    width: "100%",
    maxWidth: 620,
    margin: "0 auto",
    padding: "4px 0 16px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    fontFamily: "sans-serif",
    boxSizing: "border-box",
  },
  contactCard: {
    width: "100%",
    maxWidth: 560,
    margin: "0 auto 12px",
    boxSizing: "border-box",
    borderRadius: 18,
    border: "1px solid rgba(2,6,23,0.10)",
    backgroundColor: "#fff",
    boxShadow: "0 12px 30px rgba(2,6,23,0.055)",
    overflow: "hidden",
  },
  contactHeader: {
    appearance: "none",
    width: "100%",
    border: "none",
    background: "#FFFFFF",
    cursor: "pointer",
    padding: "15px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    textAlign: "left",
    boxSizing: "border-box",
  },
  contactHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
  },
  contactNumber: {
    width: 34,
    height: 34,
    minWidth: 34,
    borderRadius: 12,
    background: "rgba(37,99,235,0.10)",
    color: "#2563EB",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 950,
  },
  contactTitle: {
    color: "#0F172A",
    fontSize: 15,
    lineHeight: 1.25,
    fontWeight: 950,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 360,
  },
  contactSubtitle: {
    marginTop: 3,
    color: "#64748B",
    fontSize: 12.5,
    lineHeight: 1.35,
    fontWeight: 700,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 360,
  },
  expandIcon: {
    padding: "7px 10px",
    borderRadius: 999,
    background: "#F8FAFC",
    border: "1px solid rgba(2,6,23,0.08)",
    color: "#1E2A78",
    fontWeight: 900,
    fontSize: 12.5,
    whiteSpace: "nowrap",
  },
  contactForm: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    padding: "0 16px 16px",
    boxSizing: "border-box",
  },
  fieldGroup: {
    width: "100%",
    display: "grid",
    gap: 7,
    boxSizing: "border-box",
  },
  label: {
    color: "#0F172A",
    fontSize: 13,
    lineHeight: 1.35,
    fontWeight: 950,
  },
  input: {
    padding: "12px 13px",
    fontSize: 14.5,
    borderRadius: 12,
    border: "1px solid rgba(2,6,23,0.16)",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
    color: "#0F172A",
    background: "#FFFFFF",
    fontWeight: 650,
    boxShadow: "0 7px 16px rgba(2,6,23,0.032)",
  },
  hint: {
    fontSize: 12.2,
    color: "#64748B",
    marginTop: 1,
    paddingLeft: 2,
    lineHeight: 1.4,
    fontWeight: 650,
  },
  emailError: {
    color: "#e11d48",
    fontSize: 12.8,
    paddingLeft: 2,
    marginTop: 4,
    lineHeight: 1.4,
    fontWeight: 750,
  },
  blockedPanel: {
    width: "100%",
    boxSizing: "border-box",
    padding: "10px 11px",
    borderRadius: 12,
    background: "#FFF7ED",
    border: "1px solid rgba(249,115,22,0.22)",
    display: "grid",
    gap: 7,
  },
  blockedTitle: {
    color: "#9A3412",
    fontSize: 12.8,
    lineHeight: 1.4,
    fontWeight: 950,
  },
  blockedText: {
    color: "#9A3412",
    fontSize: 12.5,
    lineHeight: 1.45,
    fontWeight: 700,
  },
  reviewBtn: {
    width: "fit-content",
    border: "1px solid rgba(30,64,175,0.20)",
    background: "#EFF6FF",
    color: "#1E40AF",
    borderRadius: 10,
    padding: "8px 11px",
    fontSize: 12.5,
    fontWeight: 900,
    cursor: "pointer",
  },
  reviewBtnDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  validationWarning: {
    color: "#f97316",
    fontSize: 13,
    padding: "9px 10px",
    borderRadius: 12,
    background: "#FFF7ED",
    border: "1px solid rgba(249,115,22,0.18)",
    lineHeight: 1.45,
    fontWeight: 750,
  },
  textareaWrapper: {
    width: "100%",
    display: "grid",
    gap: 7,
    boxSizing: "border-box",
  },
  textarea: {
    fontSize: 14.5,
    minHeight: 92,
    borderRadius: 12,
    border: "1px solid rgba(2,6,23,0.16)",
    width: "100%",
    resize: "vertical",
    padding: "12px 13px",
    boxSizing: "border-box",
    outline: "none",
    color: "#0F172A",
    background: "#FFFFFF",
    fontWeight: 650,
    lineHeight: 1.5,
    boxShadow: "0 7px 16px rgba(2,6,23,0.032)",
  },
  copyMetaRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
    width: "100%",
    marginTop: "-4px",
  },
  copyBlock: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  copyIcon: {
    cursor: "pointer",
    zIndex: 1,
    width: 32,
    height: 32,
    borderRadius: 10,
    background: "#F8FAFC",
    border: "1px solid rgba(2,6,23,0.08)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  charCount: {
    fontSize: 12.5,
    color: "#64748B",
    textAlign: "left",
    fontWeight: 750,
  },
  removeBtn: {
    padding: "10px 14px",
    fontSize: 13.5,
    backgroundColor: "#FEF2F2",
    color: "#B91C1C",
    borderRadius: 12,
    border: "1px solid rgba(185,28,28,0.20)",
    cursor: "pointer",
    width: "fit-content",
    fontWeight: 900,
  },
  button: {
    padding: "11px 16px",
    fontSize: 14,
    borderRadius: 12,
    border: "none",
    minHeight: 46,
    fontWeight: 900,
    boxShadow: "0 10px 22px rgba(30,42,120,0.14)",
  },
  saveBtn: {
    padding: "11px 18px",
    fontSize: 14,
    backgroundColor: "#2563EB",
    color: "#fff",
    border: "none",
    borderRadius: 12,
    cursor: "pointer",
    minHeight: 46,
    fontWeight: 950,
    boxShadow: "0 10px 22px rgba(37,99,235,0.18)",
  },
  actions: {
    width: "100%",
    maxWidth: 560,
    display: "flex",
    flexDirection: "row",
    gap: 12,
    margin: "8px auto 0",
    justifyContent: "center",
    flexWrap: "wrap",
    boxSizing: "border-box",
  },
};
export default SmartAlertSettings;
